package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentitySet
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import kotlin.concurrent.Volatile

/**
 * [Signal] 的默认可变实现。
 * 线程安全：value 使用 @Volatile 保证可见性，写路径共用同一把 [SyncLock] 保护 subscribers 集合一致性
 * （等价迁移前的 @Synchronized / this 监视器语义）。
 */
class MutableSignal<T>(@Volatile private var value: T) :
    Signal<T>,
    Trackable {

    /** 写路径互斥锁：set/update/subscribe/unsubscribe 共用（原 @Synchronized 同一 this 监视器）。 */
    private val lock = SyncLock()

    /** 订阅者集合（按身份比较）。 */
    private val subscribers: MutableSet<Subscriber> = newIdentitySet()

    override fun get(): T {
        ReactiveContext.track(this)
        return value
    }

    override fun set(newValue: T) {
        lock.withLock {
            if (value == newValue) return
            value = newValue
            val snapshot = ArrayList(subscribers)
            if (BatchScope.inBatch()) {
                BatchScope.enqueue(snapshot)
                return
            }
            var firstError: RuntimeException? = null
            for (s in snapshot) {
                try {
                    s.onChange()
                } catch (ex: RuntimeException) {
                    if (firstError == null) firstError = ex
                    logger.error("[ui] Signal 通知订阅者失败已隔离；订阅者=${s::class.simpleName}", ex)
                }
            }
            if (firstError != null) throw firstError
        }
    }

    override fun update(updater: (T) -> T) {
        lock.withLock {
            set(updater(value))
        }
    }

    override fun subscribe(subscriber: Subscriber) {
        lock.withLock {
            subscribers.add(subscriber)
        }
    }

    override fun unsubscribe(subscriber: Subscriber) {
        lock.withLock {
            subscribers.remove(subscriber)
        }
    }

    companion object {
        private val logger = UiLog("top.wcpe.allincore.ui.runtime.reactive.MutableSignal")
    }
}
